<template>
  <ZtTooltip :content="tooltip" placement="top">
    <button :data-testid="dataTestId" :class="buttonClass" @click="handleClick" class="clipboard-button flex gap-1 items-center">
      <Icon :name="icon" size="xs" class="text-slate-500 dark:text-zinc-300" />
    </button>
  </ZtTooltip>
</template>

<script setup lang="ts">
import { Ref, watch, ref } from 'vue';
import { useClipboard } from '@vueuse/core';

import { ZtTooltip } from '@/storybook/tooltip';
import { Icon } from '@/storybook';
import { useToast } from '@/composables/useToast';
import { useViewerTrack, TrackEvents } from '@/modules/viewer/composables/useViewerTrack';

export interface ClipboardMetaButtonConfig {
  // Content and styling
  icon: string;
  tooltip: string;
  dataTestId: string;

  // Clipboard content
  content: string;

  // Feedback
  successMessage: string;
  successIcon?: string;

  // Analytics (optional)
  trackingEvent?: TrackEvents;

  // Styling options
  buttonClass?: string;
}

const props = defineProps<ClipboardMetaButtonConfig>();

const { success } = useToast();
const { trackItemOwner } = useViewerTrack();

const contentRef = ref(props.content);
const { copy, copied } = useClipboard({ source: contentRef as Ref });

watch(copied, res => {
  if (!res) return;

  // Track if tracking event provided
  if (props.trackingEvent) {
    trackItemOwner(props.trackingEvent);
  }

  // Show success message
  success(props.successMessage, {
    icon: props.successIcon || props.icon,
  });
});

// Update content ref when prop changes
watch(
  () => props.content,
  newContent => {
    contentRef.value = newContent;
  }
);

const handleClick = () => {
  copy();
};
</script>

<style scoped>
.clipboard-button:hover:not(.no-hover-bg) {
  @apply -m-0.5 p-0.5 rounded-md bg-slate-100 dark:bg-zinc-800;
}
</style>
